import { useState, useEffect } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { supabase } from '../lib/supabase'
import { useToast } from '../components/Toast'
import SEO from '../components/SEO'

export default function ResetPassword() {
  const [password, setPassword]       = useState('')
  const [confirm, setConfirm]         = useState('')
  const [loading, setLoading]         = useState(false)
  const [sessionReady, setSessionReady] = useState(false)
  const [error, setError]             = useState(null)
  const navigate = useNavigate()
  const toast    = useToast()

  useEffect(() => {
    // Supabase delivers reset tokens in the URL hash as:
    //   #access_token=xxx&refresh_token=xxx&type=recovery
    // We listen for the PASSWORD_RECOVERY event AND also parse the hash manually
    // as a fallback, since the event can fire before this component mounts.

    // Method 1: listen for auth state change
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === 'PASSWORD_RECOVERY') {
        setSessionReady(true)
        setError(null)
      }
    })

    // Method 2: check if there's already a session (page was refreshed after token exchange)
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (session) {
        setSessionReady(true)
        setError(null)
        return
      }

      // Method 3: parse hash manually and exchange tokens
      const hash = window.location.hash
      if (!hash || hash === '#') {
        // No hash at all — likely redirect URL not whitelisted in Supabase
        setError(
          'The reset link did not arrive correctly. This is usually because the redirect URL needs to be added to Supabase. ' +
          'Please add https://www.receiptchaser.com/reset-password to your Supabase redirect URLs, or request a new reset link.'
        )
        return
      }

      const params = new URLSearchParams(hash.replace('#', ''))
      const accessToken  = params.get('access_token')
      const refreshToken = params.get('refresh_token') || ''
      const type         = params.get('type')

      if (!accessToken) {
        setError('No reset token found in this link. Please request a new password reset link.')
        return
      }

      if (type !== 'recovery') {
        setError('This does not appear to be a password reset link. Please request a new one.')
        return
      }

      supabase.auth.setSession({ access_token: accessToken, refresh_token: refreshToken })
        .then(({ error: sessionError }) => {
          if (sessionError) {
            setError('This reset link has expired or has already been used. Please request a new one.')
          } else {
            setSessionReady(true)
          }
        })
    })

    return () => subscription.unsubscribe()
  }, [])

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (password.length < 8) { toast('Password must be at least 8 characters', 'error'); return }
    if (password !== confirm) { toast('Passwords do not match', 'error'); return }
    setLoading(true)
    const { error } = await supabase.auth.updateUser({ password })
    setLoading(false)
    if (error) {
      toast(error.message, 'error')
    } else {
      await supabase.auth.signOut()
      toast('Password updated. Please sign in with your new password.', 'success')
      navigate('/login')
    }
  }

  if (error) return (
    <div style={{ minHeight: '100vh', background: 'var(--surface)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
      <SEO title="Reset link issue" noindex />
      <div className="fade-up" style={{ width: '100%', maxWidth: 480, textAlign: 'center' }}>
        <div style={{ fontSize: '2rem', marginBottom: 16 }}>⚠️</div>
        <h2 style={{ marginBottom: 12 }}>Reset link issue</h2>
        <p style={{ color: 'var(--ink-muted)', marginBottom: 24, lineHeight: 1.6, fontSize: '0.9rem' }}>{error}</p>
        <Link to="/forgot-password" className="btn btn-primary">Request a new reset link</Link>
      </div>
    </div>
  )

  if (!sessionReady) return (
    <div style={{ minHeight: '100vh', background: 'var(--surface)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <SEO title="Verifying reset link" noindex />
      <div style={{ textAlign: 'center' }}>
        <div className="spinner" style={{ margin: '0 auto 16px' }} />
        <p style={{ color: 'var(--ink-muted)' }}>Verifying reset link...</p>
      </div>
    </div>
  )

  return (
    <div style={{ minHeight: '100vh', background: 'var(--surface)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
      <SEO title="Choose a new password" noindex />
      <div style={{ position: 'fixed', top: 0, right: 0, width: '40vw', height: '40vw', background: 'radial-gradient(circle, rgba(232,85,42,0.06) 0%, transparent 70%)', pointerEvents: 'none' }} />
      <div className="fade-up" style={{ width: '100%', maxWidth: 440 }}>
        <div style={{ textAlign: 'center', marginBottom: 40 }}>
          <div style={{ width: 56, height: 56, background: 'var(--accent)', borderRadius: 16, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.6rem', margin: '0 auto 16px', boxShadow: '0 8px 24px rgba(232,85,42,0.25)' }}>🔒</div>
          <h1 style={{ fontSize: '2rem', marginBottom: 6 }}>Choose a new password</h1>
          <p style={{ color: 'var(--ink-muted)', fontSize: '0.9rem' }}>Must be at least 8 characters.</p>
        </div>
        <div className="card" style={{ padding: 32 }}>
          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label htmlFor="password">New password</label>
              <input id="password" type="password" className="input" value={password}
                onChange={e => setPassword(e.target.value)} placeholder="At least 8 characters"
                minLength={8} required autoFocus />
            </div>
            <div className="form-group" style={{ marginBottom: 0 }}>
              <label htmlFor="confirm">Confirm new password</label>
              <input id="confirm" type="password" className="input" value={confirm}
                onChange={e => setConfirm(e.target.value)} placeholder="Repeat your password" required />
            </div>
            <button type="submit" className="btn btn-primary btn-lg" disabled={loading}
              style={{ width: '100%', justifyContent: 'center', marginTop: 20 }}>
              {loading ? 'Updating...' : 'Set new password'}
            </button>
          </form>
        </div>
      </div>
    </div>
  )
}
